// 186 B.3 [RULED 186 (6)] — THE NOTE MODAL: date, text, record.
//
// The Notes section is a LIST, not a form: its standing text box, date box
// and "Save note" button are gone, and "+ Add" beside the lock opens this. The
// shell is StageModal's — `ModalLayout`, the app's one modal chrome (`Mod`, the wrapper over it, went in 188 A.4)
// (the standard header X and the "✓ Save" primary; no window.prompt, no new
// shape). The section mounts it itself, the way CaseEmailsSection mounts
// AttachmentSaveModal, and does the writing: this hands back the text and the
// chosen DAY (`yyyy-mm-dd`, what DateField reads and writes) and nothing else,
// so the date's semantics stay where they were — the section's `occurredAtFor`
// (today keeps the wall clock, another day is local noon).
//
// A small, hookful modal in its own file, never inline in the section's
// render body (Bug A).

import React, { useState } from "react";
import { NO_AUTOFILL, noAutofillClass } from "../../lib/noAutofill"; // 213B §18 — no sign-ins, no contacts, in a modal's field
import type { Dispatch } from "react";
import { ModalLayout } from "../SharedUI";
import DateField from "../DateField";
import type { Translate } from "../../lib/interestRefusal";
import { FS } from "../../lib/tokens";

/** What the modal hands back: the trimmed text and the chosen day (`yyyy-mm-dd`). */
export interface NoteDraft {
  text: string;
  day: string;
}

interface Props {
  t: Translate;
  /** Today, in the reader's local calendar — the section computes it. */
  defaultDay: string;
  busy: boolean;
  onClose: () => void;
  /* Typed by React's `Dispatch` (183's finding: a function type with a named
     parameter is a base `no-unused-vars` warning in a file outside the
     baseline, which must carry zero). */
  onSave: Dispatch<NoteDraft>;
}

export default function InteractionModal({ t, defaultDay, busy, onClose, onSave }: Readonly<Props>) {
  const [text, setText] = useState("");
  const [day, setDay] = useState(defaultDay);
  const body = text.trim();
  const save = () => {
    if (!body || busy) return;
    onSave({ text: body, day });
  };
  return (
    <ModalLayout
      title={t("modal.title.add_note", { fallback: "Add Note" })}
      onClose={onClose}
      onSave={save}
      saveDisabled={busy || !body}
    >
        <>
          <div className="field">
            <label>{t("interaction.composer.date", { fallback: "Date" })}</label>
            <DateField value={day} onChange={setDay} style={{ fontSize: FS.s11 }} />
          </div>
          <div className="field">
            <label>{t("caseView.notes.title", { fallback: "Notes" })}</label>
            <textarea
              {...NO_AUTOFILL}
              className={noAutofillClass("field-input")}
              rows={4}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
              }}
              placeholder={t("interaction.composer.placeholder", {
                fallback: "Write a note — what was said, agreed or promised",
              })}
              style={{ resize: "vertical", fontSize: FS.s11 }}
            />
          </div>
        </>
    </ModalLayout>
  );
}
